import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { buildPageGraph, BUSINESS } from "../constants/stores";
import { safeJsonLd } from "../utils/jsonLd";
import { oldalNodek, egyediGraf } from "../utils/schemaSzolgaltatas";
import { useLocation } from "react-router-dom";
import { isBoltPath, boltMeta } from "../utils/boltMod";
import { withTrailingSlash } from "../utils/url";

/**
 * SEOHead – a meta-adatok ÉS a JSON-LD EGYETLEN felelőse.
 *
 * JSON-LD (Sprint1 Menet2):
 *   Oldalanként PONTOSAN EGY `<script type="application/ld+json">` blokk
 *   keletkezik, benne egyetlen @graph:
 *     • a kanonikus vállalkozás-node-ok (Organization + WebSite + 2 telephely)
 *       a `constants/stores.js`-ből – EGY igazságforrás, kézzel írt adat nélkül,
 *     • plusz az oldal saját node-jai a `schema` propból (Service, FAQPage,
 *       BreadcrumbList, Product, Article …).
 *
 *   Ezért az oldalak NE rendereljenek saját `<script type="application/ld+json">`
 *   elemet – az duplikált @id-t okozna (ez volt a korábbi hiba: a statikus
 *   public/index.html és a React-oldali gráf ugyanazt az entitást definiálta
 *   eltérő tartalommal, és a Google nemdeterminisztikusan választott).
 *
 *   AggregateRating / Review SZÁNDÉKOSAN nincs benne: a saját magunkról szóló
 *   értékelés-jelölés a Google szerint „self-serving review”, LocalBusiness-en
 *   nem jogosult rich resultra. A csillagok a Cégprofilból jönnek.
 */
// withTrailingSlash: a router-független utils/url.js-ben (ott tesztelt); innen
// továbbra is exportáljuk a régi importok miatt.
export { withTrailingSlash };

// Az alapértelmezett megosztási kép mérete. A méret-metát CSAK ehhez a képhez
// adjuk ki: korábban az index.html statikusan 1200×630-at hirdetett MINDEN
// oldalon, a terméklapok 4284×5712-es készülékfotójára is (audit #5).
const ALAP_OG_KEP = 'https://norbiphone.hu/og-image.jpg';

export default function SEOHead({
  title: eredetiCim,
  description: eredetiLeiras,
  url: eredetiUrl = "https://norbiphone.hu",
  image = ALAP_OG_KEP,
  canonical: eredetiCanonical,
  noindex: eredetiNoindex = false,
  ogType = "website", // "article" (blog), "product" (termék-oldal)
  schema = null,      // az oldal saját node-ja(i): objektum vagy tömb
  // Csak meta-felülírás, JSON-LD nélkül. A SEOAliasRoute használja: az alias
  // oldalon KÉT SEOHead renderelődik (az oldalé + a felülíró aliasé), és a
  // gráfot csak az egyik adhatja ki – különben duplikált @id keletkezne.
  metaOnly: eredetiMetaOnly = false,
}) {
  // Bolt mód (/bolt/...): szerviz-mentes hirdetési változat – noindex, saját
  // (ellenőrzött) cím/leírás, önhivatkozó canonical és NINCS JSON-LD (a
  // vállalkozás-gráf szerviz-adatokat is tartalmaz). Lásd utils/boltMod.js.
  const { pathname } = useLocation();
  const bolt = isBoltPath(pathname);
  const bm = bolt ? boltMeta(pathname, eredetiCim, eredetiLeiras) : null;
  const title = bolt ? bm.title : eredetiCim;
  const description = bolt ? bm.description : eredetiLeiras;
  const url = bolt ? `https://norbiphone.hu${pathname}` : eredetiUrl;
  const canonical = bolt ? url : eredetiCanonical;
  const noindex = bolt || eredetiNoindex;
  const metaOnly = bolt || eredetiMetaOnly;

  const fullTitle = title.includes("NorbiPhone")
    ? title
    : `${title} | NorbiPhone`;

  // A ténylegesen kiszolgált alak a ZÁRÓ PERJELES: a prerenderelt oldalak
  // könyvtárként állnak a build-ben (`/szerviz/index.html`), és az nginx
  // `try_files $uri $uri/` + `index index.html` párosa a perjel nélküli
  // alakot 301-gyel a perjelesre irányítja. Élesben mérve: `/diagnosztika`
  // → HTTP/2 301 → `/diagnosztika/`.
  //
  // A canonical és az `og:url` eddig a perjel NÉLKÜLI, tehát ÁTIRÁNYÍTÓ
  // alakra mutatott – 232 URL-ből 231 önhivatkozás nélküli volt. A canonical
  // mindig a végleges URL-re mutasson, különben a Google ellentmondó jelzést
  // kap (GSC „Alternate page with proper canonical tag”).
  const canonicalUrl = withTrailingSlash(canonical || url);
  const ogUrl = withTrailingSlash(url);

  // Canonical link közvetlen DOM manipulációval –
  // React 19 + react-helmet-async v2 kombináció nem rendereli
  // megbízhatóan a <link> tageket Helmet-en belül, ezért
  // useEffect-tel kezeljük közvetlenül.
  useEffect(() => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);
  }, [canonicalUrl]);

  // Az oldal saját node-jai + a kanonikus vállalkozás-gráf → EGY blokk.
  // A `<script>` szándékosan NEM a Helmetben van: React 19 + helmet-async v2
  // alatt a Helmet-en belüli nem-meta elemek renderelése megbízhatatlan
  // (ugyanezért kezeli a canonicalt is a fenti useEffect).
  // Kilapítva (beágyazott @graph) és a Service-ek egységesítve – lásd
  // utils/schemaSzolgaltatas.js. A végén @id szerint duplikátum-szűrés.
  const pageNodes = oldalNodek(schema, canonicalUrl);

  // WebPage node MINDEN oldalra. Enélkül a gráfban nem volt olyan entitás, ami
  // magát az OLDALT írja le: a válaszmotorok (ChatGPT/Perplexity/AI Overviews)
  // ebből kötik össze az URL-t a vállalkozással, a nyelvvel és a lap témájával.
  // A cím/leírás pontosan az, ami a <title>/<meta> – nincs második igazság.
  const webPage = {
    '@type': 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: fullTitle,
    description,
    inLanguage: 'hu-HU',
    isPartOf: { '@id': `${BUSINESS.url}/#website` },
    about: { '@id': BUSINESS.id },
    ...(image ? { primaryImageOfPage: image } : {}),
  };
  const graphJson = safeJsonLd(egyediGraf(buildPageGraph(webPage, ...pageNodes)));

  return (
    <>
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={image} />
      {image === ALAP_OG_KEP && <meta property="og:image:width" content="1200" />}
      {image === ALAP_OG_KEP && <meta property="og:image:height" content="630" />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
    </Helmet>
    {!metaOnly && (
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: graphJson }} />
    )}
    </>
  );
}
